import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, AlertOctagon, AlertTriangle, Clock, History, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** `/settings/agreement-36` (v0.4 章②、D-60)。
 *  タブ1=パターン (改定履歴つき)、タブ2=店舗別の届出状況 (未登録・旧版・失効を色分け)。
 *  設計: docs/design/v04-shift-labor-management-detail.md §2-8 */

type Pattern = {
  pattern_id: string;
  version_no: number;
  pattern_code: string;
  pattern_name: string;
  max_overtime_minutes_per_day: number | null;
  max_overtime_minutes_per_month: number | null;
  max_overtime_minutes_per_year: number | null;
  has_special_clause: boolean;
  special_max_minutes_per_month: number | null;
  special_max_minutes_per_year: number | null;
  special_max_months_per_year: number | null;
  target_work_description: string | null;
  base_month: number | null;
  notes: string | null;
  effective_from: string;
};

type NodeAgreementRow = {
  node_id: string;
  node_name: string;
  registered: boolean;
  is_outdated_version: boolean;
  is_expiring_soon: boolean;
  is_expired: boolean;
  pattern_id: string | null;
  applied_pattern_version: number | null;
  agreement_valid_from: string | null;
  agreement_valid_to: string | null;
  filed_on: string | null;
  labor_office_name: string | null;
  representative_employee_id: string | null;
  representative_name_snapshot: string | null;
  representative_selection_method: string | null;
  notes: string | null;
};

const formatMinutes = (min: number | null) => {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}時間` : `${h}時間${m}分`;
};

type PatternDraft = {
  pattern_id: string | null;
  pattern_code: string;
  pattern_name: string;
  max_overtime_minutes_per_day: string;
  max_overtime_minutes_per_month: string;
  max_overtime_minutes_per_year: string;
  has_special_clause: boolean;
  special_max_minutes_per_month: string;
  special_max_minutes_per_year: string;
  special_max_months_per_year: string;
  target_work_description: string;
  base_month: string;
  notes: string;
};

const EMPTY_PATTERN_DRAFT: PatternDraft = {
  pattern_id: null, pattern_code: '', pattern_name: '',
  max_overtime_minutes_per_day: '', max_overtime_minutes_per_month: '', max_overtime_minutes_per_year: '',
  has_special_clause: false,
  special_max_minutes_per_month: '', special_max_minutes_per_year: '', special_max_months_per_year: '',
  target_work_description: '', base_month: '', notes: '',
};

const toPatternDraft = (p: Pattern): PatternDraft => ({
  pattern_id: p.pattern_id,
  pattern_code: p.pattern_code,
  pattern_name: p.pattern_name,
  max_overtime_minutes_per_day: p.max_overtime_minutes_per_day?.toString() ?? '',
  max_overtime_minutes_per_month: p.max_overtime_minutes_per_month?.toString() ?? '',
  max_overtime_minutes_per_year: p.max_overtime_minutes_per_year?.toString() ?? '',
  has_special_clause: p.has_special_clause,
  special_max_minutes_per_month: p.special_max_minutes_per_month?.toString() ?? '',
  special_max_minutes_per_year: p.special_max_minutes_per_year?.toString() ?? '',
  special_max_months_per_year: p.special_max_months_per_year?.toString() ?? '',
  target_work_description: p.target_work_description ?? '',
  base_month: p.base_month?.toString() ?? '',
  notes: p.notes ?? '',
});

const toNullableInt = (s: string): number | null => (s.trim() === '' ? null : Number(s));

// ⚠️ TD-22: 36協定パターンの CRUD (POST/PUT /agreement-36/patterns) は requireTenantWideManage
// (routes/agreement-36.ts) で全店舗スコープを要求する。ノード別の届出 (NodesTab) は
// isResourceInPermissionScope によるノード単位判定なので own_store の店長でも自店は正当に登録できる。
// 両者を同じ変数で判定すると、店長から自店の届出登録ボタンまで消える機能退行になる。
function PatternsTab() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'agreement_36', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PatternDraft | null>(null);

  const { data, isLoading } = useQuery<{ patterns: Pattern[] }>({
    queryKey: ['agreement-36-patterns'],
    queryFn: () => apiFetch<{ patterns: Pattern[] }>('/agreement-36/patterns'),
  });
  const patterns = data?.patterns ?? [];

  const saveMut = useMutation({
    mutationFn: (d: PatternDraft) => {
      const payload = {
        pattern_code: d.pattern_code.trim(),
        pattern_name: d.pattern_name.trim(),
        max_overtime_minutes_per_day: toNullableInt(d.max_overtime_minutes_per_day),
        max_overtime_minutes_per_month: toNullableInt(d.max_overtime_minutes_per_month),
        max_overtime_minutes_per_year: toNullableInt(d.max_overtime_minutes_per_year),
        has_special_clause: d.has_special_clause,
        special_max_minutes_per_month: d.has_special_clause ? toNullableInt(d.special_max_minutes_per_month) : null,
        special_max_minutes_per_year: d.has_special_clause ? toNullableInt(d.special_max_minutes_per_year) : null,
        special_max_months_per_year: d.has_special_clause ? toNullableInt(d.special_max_months_per_year) : null,
        target_work_description: d.target_work_description.trim() === '' ? null : d.target_work_description.trim(),
        base_month: toNullableInt(d.base_month),
        notes: d.notes.trim() === '' ? null : d.notes.trim(),
      };
      return d.pattern_id
        ? apiFetch('/agreement-36/patterns', { method: 'PUT', body: JSON.stringify({ ...payload, pattern_id: d.pattern_id }) })
        : apiFetch('/agreement-36/patterns', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: (_r, d) => {
      qc.invalidateQueries({ queryKey: ['agreement-36-patterns'] });
      setDraft(null);
      toast.success(d.pattern_id ? 'パターンを改定しました (新しい版が作成されました)' : 'パターンを作成しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const isRevision = draft?.pattern_id != null;
  const draftValid = !!draft && draft.pattern_code.trim() !== '' && draft.pattern_name.trim() !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <p className="text-sm text-text-sub">36協定の内容をパターンとして登録し、店舗ごとに適用します。改定すると版が1つ上がり、既存の届出内容は履歴として保持されます。</p>
          {permsReady && !canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (パターンの登録・改定には全店舗の管理権限が必要です)。</p>}
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(EMPTY_PATTERN_DRAFT)}>
            パターンを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : patterns.length === 0 ? (
        <EmptyState message="パターンがまだありません"
          description={canManage ? '「パターンを追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {patterns.map((p) => (
            <li key={p.pattern_id} className="border border-surface-border rounded-lg bg-white p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1 basis-full lg:basis-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">{p.pattern_name}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
                      {p.pattern_code}
                    </span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">
                      第{p.version_no}版
                    </span>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-text-sub">
                    <div><dt className="inline">1日の上限: </dt><dd className="inline text-text-body">{formatMinutes(p.max_overtime_minutes_per_day)}</dd></div>
                    <div><dt className="inline">月の上限: </dt><dd className="inline text-text-body">{formatMinutes(p.max_overtime_minutes_per_month)}</dd></div>
                    <div><dt className="inline">年の上限: </dt><dd className="inline text-text-body">{formatMinutes(p.max_overtime_minutes_per_year)}</dd></div>
                  </dl>
                  {p.has_special_clause && (
                    <p className="mt-1 text-xs text-alert-text">
                      特別条項あり (月{formatMinutes(p.special_max_minutes_per_month)}・年{formatMinutes(p.special_max_minutes_per_year)}・年{p.special_max_months_per_year ?? '—'}回まで)
                    </p>
                  )}
                </div>
                {canManage && (
                  <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} onClick={() => setDraft(toPatternDraft(p))}>
                    改定
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!draft}
        title={isRevision ? 'パターンを改定' : 'パターンを追加'}
        onClose={() => setDraft(null)}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!draftValid || saveMut.isPending} loading={saveMut.isPending}
              onClick={() => draft && saveMut.mutate(draft)}>
              {isRevision ? '改定を保存' : '作成'}
            </Button>
          </>
        )}
      >
        {draft && (
          <div className="space-y-3">
            {isRevision && (
              <p className="text-xs text-alert-text bg-alert-bg border border-alert-border rounded p-2">
                改定すると新しい版が作成されます。既にこのパターンを適用している店舗の届出内容 (版) は変わりません。
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1">パターンコード*</label>
                <input type="text" value={draft.pattern_code} disabled={isRevision} maxLength={50}
                  onChange={(e) => setDraft({ ...draft, pattern_code: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring disabled:bg-surface-sunken disabled:text-text-sub" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">パターン名*</label>
                <input type="text" value={draft.pattern_name} maxLength={200}
                  onChange={(e) => setDraft({ ...draft, pattern_name: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1">1日の上限 (分)</label>
                <input type="number" min={0} value={draft.max_overtime_minutes_per_day}
                  onChange={(e) => setDraft({ ...draft, max_overtime_minutes_per_day: e.target.value })}
                  className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">月の上限 (分)</label>
                <input type="number" min={0} value={draft.max_overtime_minutes_per_month}
                  onChange={(e) => setDraft({ ...draft, max_overtime_minutes_per_month: e.target.value })}
                  className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">年の上限 (分)</label>
                <input type="number" min={0} value={draft.max_overtime_minutes_per_year}
                  onChange={(e) => setDraft({ ...draft, max_overtime_minutes_per_year: e.target.value })}
                  className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={draft.has_special_clause}
                onChange={(e) => setDraft({ ...draft, has_special_clause: e.target.checked })} />
              特別条項を設定する
            </label>
            {draft.has_special_clause && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pl-2 border-l-2 border-surface-border">
                <div>
                  <label className="block text-xs text-text-sub mb-1">特別条項 月の上限 (分)</label>
                  <input type="number" min={0} value={draft.special_max_minutes_per_month}
                    onChange={(e) => setDraft({ ...draft, special_max_minutes_per_month: e.target.value })}
                    className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                </div>
                <div>
                  <label className="block text-xs text-text-sub mb-1">特別条項 年の上限 (分)</label>
                  <input type="number" min={0} value={draft.special_max_minutes_per_year}
                    onChange={(e) => setDraft({ ...draft, special_max_minutes_per_year: e.target.value })}
                    className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                </div>
                <div>
                  <label className="block text-xs text-text-sub mb-1">年間の適用回数上限</label>
                  <input type="number" min={0} max={12} value={draft.special_max_months_per_year}
                    onChange={(e) => setDraft({ ...draft, special_max_months_per_year: e.target.value })}
                    className="w-full appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">起算月 (1〜12)</label>
              <input type="number" min={1} max={12} value={draft.base_month}
                onChange={(e) => setDraft({ ...draft, base_month: e.target.value })}
                className="w-32 appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">対象業務</label>
              <textarea rows={2} maxLength={500} value={draft.target_work_description}
                onChange={(e) => setDraft({ ...draft, target_work_description: e.target.value })}
                className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">備考</label>
              <textarea rows={2} maxLength={1000} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AgreementStatusBadges({ node }: { node: NodeAgreementRow }) {
  const badges: { key: string; icon: typeof AlertOctagon; label: string; cls: string }[] = [];
  if (!node.registered) {
    badges.push({ key: 'unregistered', icon: AlertOctagon, label: '未登録', cls: 'bg-danger-bg text-danger-text border-danger-border' });
  } else {
    if (node.is_expired) badges.push({ key: 'expired', icon: AlertTriangle, label: '失効済み', cls: 'bg-danger-bg text-danger-text border-danger-border' });
    else if (node.is_expiring_soon) badges.push({ key: 'expiring', icon: Clock, label: '失効間近', cls: 'bg-alert-bg text-alert-text border-alert-border' });
    if (node.is_outdated_version) badges.push({ key: 'outdated', icon: History, label: '旧版で届出', cls: 'bg-alert-bg text-alert-text border-alert-border' });
    if (!node.is_expired && !node.is_expiring_soon && !node.is_outdated_version) {
      badges.push({ key: 'ok', icon: CheckCircle2, label: '正常', cls: 'bg-success-bg text-success-text border-success-border' });
    }
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((b) => {
        const Icon = b.icon;
        return (
          <span key={b.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs whitespace-nowrap border ${b.cls}`}>
            <Icon size={12} aria-hidden /> {b.label}
          </span>
        );
      })}
    </div>
  );
}

type NodeAgreementDraft = {
  node_id: string;
  node_name: string;
  pattern_id: string;
  agreement_valid_from: string;
  agreement_valid_to: string;
  filed_on: string;
  labor_office_name: string;
  representative_name_snapshot: string;
  representative_selection_method: string;
  notes: string;
};

function NodesTab() {
  // ⚠️ TD-22: PUT /agreement-36/nodes/:nodeId は isResourceInPermissionScope でノード単位に
  // 判定するため、own_store の店長でも自店の届出は正当に登録できる。ここを全店舗要求にしない。
  const user = useAuthStore((s) => s.user);
  const canManage = hasPermission(user?.effective_permissions, 'agreement_36', 'manage');
  const qc = useQueryClient();
  const [editing, setEditing] = useState<NodeAgreementDraft | null>(null);

  const { data, isLoading } = useQuery<{ nodes: NodeAgreementRow[] }>({
    queryKey: ['agreement-36-nodes'],
    queryFn: () => apiFetch<{ nodes: NodeAgreementRow[] }>('/agreement-36/nodes'),
  });
  const { data: patternsData } = useQuery<{ patterns: Pattern[] }>({
    queryKey: ['agreement-36-patterns'],
    queryFn: () => apiFetch<{ patterns: Pattern[] }>('/agreement-36/patterns'),
  });
  const patterns = patternsData?.patterns ?? [];
  const patternNameById = useMemo(() => new Map(patterns.map((p) => [p.pattern_id, p.pattern_name])), [patterns]);

  const nodes = useMemo(
    () => [...(data?.nodes ?? [])].sort((a, b) => a.node_name.localeCompare(b.node_name, 'ja')),
    [data],
  );

  const saveMut = useMutation({
    mutationFn: (d: NodeAgreementDraft) => apiFetch(`/agreement-36/nodes/${encodeURIComponent(d.node_id)}`, {
      method: 'PUT',
      body: JSON.stringify({
        pattern_id: d.pattern_id,
        agreement_valid_from: d.agreement_valid_from,
        agreement_valid_to: d.agreement_valid_to,
        filed_on: d.filed_on.trim() === '' ? null : d.filed_on,
        labor_office_name: d.labor_office_name.trim() === '' ? null : d.labor_office_name.trim(),
        representative_name_snapshot: d.representative_name_snapshot.trim() === '' ? null : d.representative_name_snapshot.trim(),
        representative_selection_method: d.representative_selection_method.trim() === '' ? null : d.representative_selection_method.trim(),
        notes: d.notes.trim() === '' ? null : d.notes.trim(),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreement-36-nodes'] });
      setEditing(null);
      toast.success('届出情報を保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  // ⚠️ design-and-code レビュー CRITICAL: 以前は既存値を空文字で初期化しており、「更新」を押すたびに
  // 未編集の届出日・提出先労基署・過半数代表者・備考が無警告で null 上書きされていた (GET /nodes が
  // 元々これらを返していなかったのが根本原因、routes/agreement-36.ts 側も併せて修正済み)。
  const openEdit = (node: NodeAgreementRow) => setEditing({
    node_id: node.node_id,
    node_name: node.node_name,
    pattern_id: node.pattern_id ?? patterns[0]?.pattern_id ?? '',
    agreement_valid_from: node.agreement_valid_from ?? '',
    agreement_valid_to: node.agreement_valid_to ?? '',
    filed_on: node.filed_on ?? '',
    labor_office_name: node.labor_office_name ?? '',
    representative_name_snapshot: node.representative_name_snapshot ?? '',
    representative_selection_method: node.representative_selection_method ?? '',
    notes: node.notes ?? '',
  });

  const draftValid = !!editing && editing.pattern_id !== '' && editing.agreement_valid_from !== '' && editing.agreement_valid_to !== '';

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">
        店舗ごとの36協定の届出状況です。未登録・旧版での届出・失効間近/失効済みは色分けして表示します。
      </p>
      {patterns.length === 0 && (
        <p className="text-xs text-alert-text bg-alert-bg border border-alert-border rounded p-2">
          先に「パターン」タブでパターンを1件以上登録してください。
        </p>
      )}
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : nodes.length === 0 ? (
        <EmptyState message="対象の店舗がありません" />
      ) : (
        <ul className="space-y-2">
          {nodes.map((node) => (
            <li key={node.node_id} className="border border-surface-border rounded-lg bg-white p-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1 basis-full sm:basis-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-text-body break-words">{node.node_name}</span>
                  <AgreementStatusBadges node={node} />
                </div>
                {node.registered && (
                  <p className="text-xs text-text-sub">
                    {patternNameById.get(node.pattern_id ?? '') ?? node.pattern_id} (第{node.applied_pattern_version}版) ・
                    有効期限: {node.agreement_valid_to ?? '—'}
                  </p>
                )}
              </div>
              {canManage && (
                <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} disabled={patterns.length === 0} onClick={() => openEdit(node)}>
                  {node.registered ? '更新' : '登録'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!editing}
        title={editing ? `${editing.node_name} の届出情報` : ''}
        onClose={() => setEditing(null)}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!draftValid || saveMut.isPending} loading={saveMut.isPending}
              onClick={() => editing && saveMut.mutate(editing)}>
              保存
            </Button>
          </>
        )}
      >
        {editing && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">適用パターン*</label>
              <Select value={editing.pattern_id} onValueChange={(v) => setEditing({ ...editing, pattern_id: v })}>
                <SelectTrigger aria-label="適用パターン"><SelectValue placeholder="選択してください" /></SelectTrigger>
                <SelectContent>
                  {patterns.map((p) => <SelectItem key={p.pattern_id} value={p.pattern_id}>{p.pattern_name} ({p.pattern_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1">協定の有効期間 (開始)*</label>
                <input type="date" value={editing.agreement_valid_from}
                  onChange={(e) => setEditing({ ...editing, agreement_valid_from: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">協定の有効期間 (終了)*</label>
                <input type="date" value={editing.agreement_valid_to}
                  onChange={(e) => setEditing({ ...editing, agreement_valid_to: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1">届出日</label>
                <input type="date" value={editing.filed_on}
                  onChange={(e) => setEditing({ ...editing, filed_on: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">提出先労基署</label>
                <input type="text" maxLength={200} value={editing.labor_office_name}
                  onChange={(e) => setEditing({ ...editing, labor_office_name: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1">過半数代表者 氏名</label>
                <input type="text" maxLength={120} value={editing.representative_name_snapshot}
                  onChange={(e) => setEditing({ ...editing, representative_name_snapshot: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">選出方法</label>
                <input type="text" maxLength={200} value={editing.representative_selection_method}
                  onChange={(e) => setEditing({ ...editing, representative_selection_method: e.target.value })}
                  className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">備考</label>
              <textarea rows={2} maxLength={1000} value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="w-full rounded border border-surface-border px-2.5 py-2 text-sm focus-ring" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function Agreement36SettingsPage() {
  const [tab, setTab] = useState<'patterns' | 'nodes'>('patterns');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">36協定</h2>
      </div>
      <div className="border-b border-surface-border">
        <nav className="flex gap-1" aria-label="36協定タブ">
          {([
            { key: 'patterns' as const, label: 'パターン' },
            { key: 'nodes' as const, label: '店舗別届出状況' },
          ]).map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              aria-current={tab === t.key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === t.key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'patterns' ? <PatternsTab /> : <NodesTab />}
    </div>
  );
}
